import "./SelectedGym.scss";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router";
import { openErrorMessage } from "../../../store/message/messageSlice";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { useTranslation } from "react-i18next";
import Loader from "../../../components/shared/Loader/Loader";
import Slider from "react-slick";
import heartAddSvg from "../../../assets/images/icons/heartAdd.svg";
import heartSvg from "../../../assets/images/icons/heart.svg";

import useAppSelector from "../../../hooks/useAppSelector";
import { useGymsMutations, useSavedPlacedMutations } from "../../../api/mutations";
import {
  useGetGymByIdQuery,
  useGetSettingsQuery,
  useGetUserSubscriptionQuery,
} from "../../../api/queries";
import dayjs from "dayjs";
import { useGetAllReviewsByGymIdQuery } from "../../../api/queries";
import markIcon from "../../../assets/images/icons/main-marker.svg";
import { Marker } from "@react-google-maps/api";
import Map from "../../../components/Map/Map";

import { ScheduleTab } from "./ScheduleTab";
import { InfoTab } from "./InfoTab";
import { OpenGymsCalendar } from "./OpenGymsCalendarTab";
import { ComingSoon } from "../../../components/ComingSoon/ComingSoon";
import { log } from "console";

const TABS = [
  { id: 0, localization: "Info" },
  { id: 1, localization: "Schedule" },
  { id: 2, localization: "OpenGymCalendar" },
];

const SelectedGym = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useAppSelector((state) => state.userProfileSlice);
  const { deletePlace, savePlace } = useSavedPlacedMutations();
  const { notificationForComingSoonGym } = useGymsMutations();

  const today = dayjs().format("YYYY-MM-DD");
  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: gym,
    isLoading,
    isError,
    refetch,
  } = useGetGymByIdQuery({ id: id as string, userId: user?.id || null });

  const { data: userSubscription } = useGetUserSubscriptionQuery(user?.id || null);
  const { data: settings = null } = useGetSettingsQuery();

  const {
    data: reviews = null,
    isLoading: isLoadingReviews,
    isFetching,
  } = useGetAllReviewsByGymIdQuery({
    gymListingId: gym?.id || "",
    pageSize: 2,
    page: currentPage,
  });

  const totalPages = reviews ? Math.ceil((reviews.totalCount ?? 0) / 2) : 1;

  const handleNextPage = () => {
    2;
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };
  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  useEffect(() => {
    if (!id) navigate("/gyms-location");
  }, []);

  useEffect(() => {
    if (isError) {
      navigate("/gyms-location");
      dispatch(openErrorMessage(t("SOMETHING_WENT_WRONG")));
    }
  }, [isError]);

  const onSaveOrDeletePlace = async () => {
    if (!gym) return;
    if (gym.savedPlace) {
      deletePlace(
        { id: gym.savedPlace.id },
        {
          onSettled: () => {
            refetch();
          },
        }
      );
    } else {
      savePlace(
        { gymListingId: gym.id },
        {
          onSettled: () => {
            refetch();
          },
        }
      );
    }
  };

  const onNotifyMe = (id: string) => {
    console.log("here");

    if (!id) return;

    notificationForComingSoonGym(id, {
      onSuccess: () => {
        refetch();
      },
      onError: (error) => {
        console.error("Error sending notification request:", error);
      },
    });
  };

  if (isLoading || !gym) return <Loader />;

  return (
    <div className="container">
      <div className="gym-listing-preview-wrapper">
        {/* LEFT SIDE */}
        <div className="info">
          {/* === SLIDER === */}
          <div className="slider-container">
            <Slider {...settings}>
              {gym?.imagePaths.map((el, index) => (
                <div className="slider-item" key={index}>
                  <div className="slider-image" style={{ backgroundImage: `url("${el}")` }} />
                </div>
              ))}
            </Slider>
          </div>

          {/* === TABS === */}
          <ul className="tabs-wrapper">
            {TABS.map((tab, index) => (
              <li
                key={tab.id}
                className={`tab ${tab.id === activeTab.id ? "active" : ""}`}
                onClick={() => {
                  if (gym.comingSoon && index > 0) return;
                  setActiveTab(tab);
                }}
              >
                {t(tab.localization)}
              </li>
            ))}

            <div className="tabs-wrapper-btns">
              {user && (
                <button className="circle" onClick={onSaveOrDeletePlace}>
                  <img
                    width={20}
                    height={20}
                    src={gym?.savedPlace ? heartSvg : heartAddSvg}
                    alt="heart"
                  />
                </button>
              )}
            </div>
          </ul>

          {activeTab.id === 0 && (
            <InfoTab
              gym={gym}
              userSubscription={userSubscription || null}
              settings={settings}
              isLoadingReviews={isLoadingReviews}
              isFetching={isFetching}
              reviews={reviews}
              currentPage={currentPage}
              totalPages={totalPages}
              handlePrevPage={handlePrevPage}
              handleNextPage={handleNextPage}
            />
          )}

          {activeTab.id === 1 && (
            <ScheduleTab
              settings={settings}
              gymId={gym.id}
              userSubscription={userSubscription || null}
            />
          )}

          {activeTab.id === 2 && (
            <OpenGymsCalendar
              settings={settings}
              gymId={gym.id}
              userSubscription={userSubscription || null}
            />
          )}

          {gym.comingSoon ? null : (
            <>
              {/* === HOW TO PREPARE === */}
              <div className="prepare-wrapper">
                <h3 className="title">{t("HowToPrepare")}</h3>
                <p className="description">{t("HowToPrepareDesc")}</p>
              </div>

              {/* === AMENITIES === */}
              {gym?.facilityOffers && JSON.parse(gym?.facilityOffers).length > 0 ? (
                <ul className="services-wrapper">
                  <h3 className="title">{t("Amenities")}</h3>
                  <div className="wrapper">
                    {JSON.parse(gym?.facilityOffers).map((el: string) => (
                      <li key={el}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M21.546 5.11107C21.8272 5.39236 21.9852 5.77382 21.9852 6.17157C21.9852 6.56931 21.8272 6.95077 21.546 7.23207L10.303 18.4751C10.1544 18.6237 9.97805 18.7416 9.7839 18.822C9.58976 18.9024 9.38167 18.9438 9.17153 18.9438C8.96138 18.9438 8.75329 18.9024 8.55915 18.822C8.365 18.7416 8.1886 18.6237 8.04003 18.4751L2.45403 12.8901C2.31076 12.7517 2.19649 12.5862 2.11787 12.4032C2.03926 12.2202 1.99788 12.0233 1.99615 11.8242C1.99442 11.625 2.03237 11.4275 2.10779 11.2431C2.18322 11.0588 2.29459 10.8913 2.43543 10.7505C2.57627 10.6096 2.74375 10.4983 2.92809 10.4228C3.11244 10.3474 3.30996 10.3095 3.50913 10.3112C3.7083 10.3129 3.90513 10.3543 4.08813 10.4329C4.27114 10.5115 4.43666 10.6258 4.57503 10.7691L9.17103 15.3651L19.424 5.11107C19.5633 4.97168 19.7287 4.8611 19.9108 4.78566C20.0928 4.71022 20.288 4.67139 20.485 4.67139C20.6821 4.67139 20.8772 4.71022 21.0593 4.78566C21.2413 4.8611 21.4067 4.97168 21.546 5.11107Z"
                            fill="#FF8C42"
                          />
                        </svg>
                        {t(el)}
                      </li>
                    ))}
                  </div>
                </ul>
              ) : null}
            </>
          )}
        </div>

        {/* RIGHT SIDE */}
        <div className={"map"}>
          <div className={"map-wrapper"}>
            <Map
              center={{
                lat: gym.lat,
                lng: gym.long,
              }}
            >
              <Marker icon={markIcon} position={{ lat: gym.lat, lng: gym.long }} />
            </Map>
          </div>

          <p className={"text"}>
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.1641 17.5817V15.2951C14.1641 12.3551 16.5441 9.96338 19.4957 9.96338H21.7824"
                stroke="#FF8C42"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.55664 9.97498H8.84333C11.7833 9.97498 14.175 12.355 14.175 15.3066V16.065V20.125"
                stroke="#FF8C42"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.33045 7.875L6.23047 9.975L8.33045 12.075"
                stroke="#FF8C42"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M19.6709 7.875L21.7709 9.975L19.6709 12.075"
                stroke="#FF8C42"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.4997 25.6667H17.4997C23.333 25.6667 25.6663 23.3334 25.6663 17.5V10.5C25.6663 4.66671 23.333 2.33337 17.4997 2.33337H10.4997C4.66634 2.33337 2.33301 4.66671 2.33301 10.5V17.5C2.33301 23.3334 4.66634 25.6667 10.4997 25.6667Z"
                stroke="#FF8C42"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {gym.country}, {gym.city}, {gym.street} {gym.streetNumber}, {gym.postcode}
          </p>
          {/* <p className={"text"}>
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M25.6313 21.385C25.6313 21.805 25.538 22.2367 25.3397 22.6567C25.1413 23.0767 24.8847 23.4734 24.5463 23.8467C23.9747 24.4767 23.3447 24.9317 22.633 25.2234C21.933 25.515 21.1747 25.6667 20.358 25.6667C19.168 25.6667 17.8963 25.3867 16.5547 24.815C15.213 24.2434 13.8713 23.4734 12.5413 22.505C11.1997 21.525 9.92801 20.44 8.71467 19.2384C7.51301 18.025 6.42801 16.7534 5.45967 15.4234C4.50301 14.0934 3.73301 12.7634 3.17301 11.445C2.61301 10.115 2.33301 8.84337 2.33301 7.63004C2.33301 6.83671 2.47301 6.07837 2.75301 5.37837C3.03301 4.66671 3.47634 4.01337 4.09467 3.43004C4.84134 2.69504 5.65801 2.33337 6.52134 2.33337C6.84801 2.33337 7.17467 2.40337 7.46634 2.54337C7.76967 2.68337 8.03801 2.89337 8.24801 3.19671L10.9547 7.01171C11.1647 7.30337 11.3163 7.57171 11.4213 7.82837C11.5263 8.07337 11.5847 8.31837 11.5847 8.54004C11.5847 8.82004 11.503 9.10004 11.3397 9.36837C11.188 9.63671 10.9663 9.91671 10.6863 10.1967L9.79967 11.1184C9.67134 11.2467 9.61301 11.3984 9.61301 11.585C9.61301 11.6784 9.62467 11.76 9.64801 11.8534C9.68301 11.9467 9.71801 12.0167 9.74134 12.0867C9.95134 12.4717 10.313 12.9734 10.8263 13.58C11.3513 14.1867 11.9113 14.805 12.518 15.4234C13.148 16.0417 13.7547 16.6134 14.373 17.1384C14.9797 17.6517 15.4813 18.0017 15.878 18.2117C15.9363 18.235 16.0063 18.27 16.088 18.305C16.1813 18.34 16.2747 18.3517 16.3797 18.3517C16.578 18.3517 16.7297 18.2817 16.858 18.1534L17.7447 17.2784C18.0363 16.9867 18.3163 16.765 18.5847 16.625C18.853 16.4617 19.1213 16.38 19.413 16.38C19.6347 16.38 19.868 16.4267 20.1247 16.5317C20.3813 16.6367 20.6497 16.7884 20.9413 16.9867L24.803 19.7284C25.1063 19.9384 25.3163 20.1834 25.4447 20.475C25.5613 20.7667 25.6313 21.0584 25.6313 21.385Z"
                stroke="#FF8C42"
                strokeWidth="1.5"
                strokeMiterlimit="10"
              />
            </svg>
            +3041523141
          </p> */}

          {/* <p className={"text"}>
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.4997 25.6667H17.4997C23.333 25.6667 25.6663 23.3334 25.6663 17.5V10.5C25.6663 4.66671 23.333 2.33337 17.4997 2.33337H10.4997C4.66634 2.33337 2.33301 4.66671 2.33301 10.5V17.5C2.33301 23.3334 4.66634 25.6667 10.4997 25.6667Z"
                stroke="#FF8C42"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14.0003 18.0833C16.2555 18.0833 18.0837 16.2551 18.0837 14C18.0837 11.7448 16.2555 9.91663 14.0003 9.91663C11.7452 9.91663 9.91699 11.7448 9.91699 14C9.91699 16.2551 11.7452 18.0833 14.0003 18.0833Z"
                stroke="#FF8C42"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.5751 8.16671H20.5886"
                stroke="#FF8C42"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Gym.com
          </p> */}

          {/* <p className={"text"}>
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.3333 10.85V14.2333H19.3667C19.6 14.2333 19.7167 14.4667 19.7167 14.7L19.25 16.9167C19.25 17.0333 19.0167 17.15 18.9 17.15H16.3333V25.6667H12.8333V17.2667H10.85C10.6167 17.2667 10.5 17.15 10.5 16.9167V14.7C10.5 14.4667 10.6167 14.35 10.85 14.35H12.8333V10.5C12.8333 8.51667 14.35 7 16.3333 7H19.4833C19.7167 7 19.8333 7.11667 19.8333 7.35V10.15C19.8333 10.3833 19.7167 10.5 19.4833 10.5H16.6833C16.45 10.5 16.3333 10.6167 16.3333 10.85Z"
                stroke="#FF8C42"
                strokeWidth="1.3"
                strokeMiterlimit="10"
                strokeLinecap="round"
              />
              <path
                d="M17.4997 25.6667H10.4997C4.66634 25.6667 2.33301 23.3334 2.33301 17.5V10.5C2.33301 4.66671 4.66634 2.33337 10.4997 2.33337H17.4997C23.333 2.33337 25.6663 4.66671 25.6663 10.5V17.5C25.6663 23.3334 23.333 25.6667 17.4997 25.6667Z"
                stroke="#FF8C42"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Visit profile
          </p> */}

          {/* <div className={"hours-wrapper"}>
            <p className={"text"}>
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_24742_15317)">
                  <path
                    d="M13.9997 2.33337C20.4432 2.33337 25.6663 7.55654 25.6663 14C25.6663 20.4435 20.4432 25.6667 13.9997 25.6667C7.55618 25.6667 2.33301 20.4435 2.33301 14C2.33301 7.55654 7.55618 2.33337 13.9997 2.33337ZM13.9997 4.66671C11.5243 4.66671 9.15035 5.65004 7.40001 7.40038C5.64967 9.15072 4.66634 11.5247 4.66634 14C4.66634 16.4754 5.64967 18.8494 7.40001 20.5997C9.15035 22.35 11.5243 23.3334 13.9997 23.3334C16.475 23.3334 18.849 22.35 20.5993 20.5997C22.3497 18.8494 23.333 16.4754 23.333 14C23.333 11.5247 22.3497 9.15072 20.5993 7.40038C18.849 5.65004 16.475 4.66671 13.9997 4.66671ZM13.9997 7.00004C14.2854 7.00008 14.5612 7.10499 14.7748 7.29488C14.9883 7.48476 15.1247 7.74641 15.1582 8.03021L15.1663 8.16671V13.517L18.3245 16.6752C18.5337 16.8852 18.6552 17.1669 18.6643 17.4632C18.6733 17.7594 18.5693 18.048 18.3732 18.2704C18.1772 18.4927 17.9039 18.6321 17.6088 18.6602C17.3137 18.6883 17.019 18.603 16.7845 18.4217L16.6748 18.3249L13.1748 14.8249C12.9935 14.6434 12.8771 14.4072 12.8435 14.1529L12.833 14V8.16671C12.833 7.85729 12.9559 7.56054 13.1747 7.34175C13.3935 7.12296 13.6903 7.00004 13.9997 7.00004Z"
                    fill="#FF8C42"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_24742_15317">
                    <rect width="28" height="28" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              {t("WorkingHours")}
            </p>

            <div className={"rule"}>
              <p className={"day"}>Monday</p>
              <p className={"time"}>08:00 - 12:00</p>
            </div>
            <div className={"rule"}>
              <p className={"day"}>Tuesday</p>
              <p className={"time"}>09:00 - 13:00</p>
            </div>
            <div className={"rule"}>
              <p className={"day"}>Wednesday</p>
              <p className={"time"}>10:00 - 14:00</p>
            </div>
            <div className={"rule"}>
              <p className={"day"}>Thursday</p>
              <p className={"time"}>11:00 - 15:00</p>
            </div>
            <div className={"rule"}>
              <p className={"day"}>Friday</p>
              <p className={"time"}>12:00 - 16:00</p>
            </div>
            <div className={"rule"}>
              <p className={"day"}>Saturday</p>
              <p className={"time"}>09:00 - 11:00</p>
            </div>
            <div className={"rule"}>
              <p className={"day"}>Sunday</p>
              <p className={"time"}>10:00 - 12:00</p>
            </div>
          </div> */}
        </div>
      </div>

      {gym.comingSoon ? (
        <div style={{ marginBottom: 150, marginTop: 50 }}>
          <ComingSoon
            isSubscribed={gym.subscribedToComingSoon}
            onNotifyMe={() => onNotifyMe(gym.id)}
          />
        </div>
      ) : null}
    </div>
  );
};

export default SelectedGym;
